/**
 * Settlement rail configuration for cold-start → clearinghouse.
 * Non-custodial: users pay from their wallets into public contracts / DePay receivers.
 */

const trim = v => (typeof v === 'string' && v.trim() ? v.trim() : null);

const looksLikePlaceholder = value => {
  if (!value) return true;
  const v = String(value).trim();
  return (
    !v ||
    v.includes('111111') ||
    /placeholder|changeme|betterexchangetreasury|sellerescrow/i.test(v)
  );
};

/** Solana USDC mainnet mint */
export const SOLANA_USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

/** Common EVM USDC (native) — override via env when launching a chain */
export const EVM_USDC_BY_CHAIN = {
  ethereum: trim(process.env.REACT_APP_USDC_ETHEREUM) || '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  polygon: trim(process.env.REACT_APP_USDC_POLYGON) || '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
  base: trim(process.env.REACT_APP_USDC_BASE) || '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
};

export const getTreasuryAddress = () =>
  trim(process.env.REACT_APP_COMPANY_B_TREASURY_ADDRESS);

export const getDefaultSellerReceiver = () =>
  trim(process.env.REACT_APP_SELLER_ESCROW_RECEIVER);

/** Allow DePay widget with placeholder receivers for initial prod tests. */
export const allowDePayPlaceholders = () => {
  const raw = process.env.REACT_APP_DEPAY_ALLOW_PLACEHOLDERS;
  if (raw === 'false' || raw === '0') return false;
  // Default true until real wallets are set (operator cold-start)
  return true;
};

export const isDePayReady = () => {
  const treasury = getTreasuryAddress();
  const seller = getDefaultSellerReceiver();
  if (allowDePayPlaceholders()) {
    return Boolean(treasury || seller || allowDePayPlaceholders());
  }
  return !looksLikePlaceholder(treasury) && !looksLikePlaceholder(seller);
};

/**
 * DePay accept[] entries — Solana USDC always; optional EVM chains via REACT_APP_DEPAY_CHAINS.
 * Example: REACT_APP_DEPAY_CHAINS=solana,polygon,base
 */
export const getDePayAcceptTokens = ({ amount, receiver } = {}) => {
  const settleTo = receiver || getDefaultSellerReceiver() || 'SellerEscrow111111111111111111111111111111';
  const feeReceiver =
    getTreasuryAddress() || 'BetterExchangeTreasury1111111111111111111111111';
  const chainsRaw = trim(process.env.REACT_APP_DEPAY_CHAINS) || 'solana';
  const chains = chainsRaw.split(',').map(c => c.trim().toLowerCase()).filter(Boolean);

  const fee = { amount: '0.5%', receiver: feeReceiver };
  const accept = [];

  if (chains.includes('solana')) {
    accept.push({
      blockchain: 'solana',
      token: SOLANA_USDC_MINT,
      amount,
      receiver: settleTo,
      fee,
    });
  }

  ['ethereum', 'polygon', 'base'].forEach(chain => {
    if (!chains.includes(chain)) return;
    const token = EVM_USDC_BY_CHAIN[chain];
    if (!token) return;
    // EVM receivers must be 0x addresses — skip if Solana-style placeholder
    if (!String(settleTo).startsWith('0x') && !allowDePayPlaceholders()) return;
    accept.push({
      blockchain: chain,
      token,
      amount,
      receiver: settleTo.startsWith('0x') ? settleTo : feeReceiver,
      fee: feeReceiver.startsWith('0x') ? fee : undefined,
    });
  });

  return accept.length ? accept : [
    {
      blockchain: 'solana',
      token: SOLANA_USDC_MINT,
      amount,
      receiver: settleTo,
      fee,
    },
  ];
};

/** UTT / escrow contract readiness from env (mainnet or Anvil). */
export const getUttRailStatus = () => {
  const escrow =
    trim(process.env.REACT_APP_ESCROW_CONTRACT_ADDRESS) ||
    trim(process.env.ESCROW_CONTRACT_ADDRESS);
  const hub = trim(process.env.REACT_APP_HUB_ORDER_CONTRACT_ADDRESS) ||
    trim(process.env.HUB_ORDER_CONTRACT_ADDRESS);
  const spoke =
    trim(process.env.REACT_APP_SPOKE_ORDER_CONTRACT_ADDRESS) ||
    trim(process.env.SPOKE_ORDER_CONTRACT_ADDRESS);
  const rpc = trim(process.env.REACT_APP_CHAIN_RPC_URL) || trim(process.env.CHAIN_RPC_URL);

  const escrowOk = escrow && !looksLikePlaceholder(escrow);
  const hubOk = hub && !looksLikePlaceholder(hub);

  return {
    escrowAddress: escrow || null,
    hubAddress: hub || null,
    spokeAddress: spoke || null,
    rpcUrl: rpc || null,
    escrowConfigured: Boolean(escrowOk),
    hubConfigured: Boolean(hubOk),
    spokeConfigured: Boolean(spoke && !looksLikePlaceholder(spoke)),
    mode: escrowOk && hubOk ? 'utt_ready' : escrowOk ? 'escrow_only' : 'cash_pin_default',
    label:
      escrowOk && hubOk
        ? 'UTT hub/spoke addresses configured'
        : escrowOk
          ? 'Escrow contract configured'
          : 'Cash + PIN default (contracts not set)',
  };
};

export default {
  getDePayAcceptTokens,
  getUttRailStatus,
  isDePayReady,
  allowDePayPlaceholders,
};
